import { useContext, useEffect, useRef } from 'react'
import { ExternalLink } from '../../ui-lib/link'
import { getConfig } from '../../../config'
import { gnarSpec } from '@lib/tracking/gnar'
import {
  GRAMMAR_CHECK_SLUG,
  PLAGIARISM_CHECKER_SLUG,
  PARAPHRASING_TOOL_SLUG,
  AI_DETECTOR_SLUG,
  AI_HUMANIZER_SLUG,
} from '../../../lib/constants'
import { PageContext } from '../../../contexts/page-context'
import cn from 'classnames'
import {
  GrammarCheckIcon,
  PlagiarismCheckerIcon,
  ParaphrasingToolIcon,
  AiDetectorIcon,
  AiHumanizerIcon,
} from '../../tool/icons'

import styles from './tools-secondary-nav.module.scss'

const baseUrl = getConfig().urls.main

const toolsSecondaryNavItems = [
  {
    label: 'Grammar Check',
    href: `${baseUrl}/${GRAMMAR_CHECK_SLUG}`,
    icon: GrammarCheckIcon,
    slug: GRAMMAR_CHECK_SLUG,
  },
  {
    label: 'Plagiarism Checker',
    href: `${baseUrl}/${PLAGIARISM_CHECKER_SLUG}`,
    icon: PlagiarismCheckerIcon,
    slug: PLAGIARISM_CHECKER_SLUG,
  },
  {
    label: 'Paraphrasing Tool',
    href: `${baseUrl}/${PARAPHRASING_TOOL_SLUG}`,
    icon: ParaphrasingToolIcon,
    slug: PARAPHRASING_TOOL_SLUG,
  },
  {
    label: 'AI Detector',
    href: `${baseUrl}/${AI_DETECTOR_SLUG}`,
    icon: AiDetectorIcon,
    slug: AI_DETECTOR_SLUG,
  },
  {
    label: 'AI Humanizer',
    href: `${baseUrl}/${AI_HUMANIZER_SLUG}`,
    icon: AiHumanizerIcon,
    slug: AI_HUMANIZER_SLUG,
  },
]

interface ToolsSecondaryNavBannerProps {
  mobileEnabled?: boolean
}

export const ToolsSecondaryNavBanner: React.FC<ToolsSecondaryNavBannerProps> = ({ mobileEnabled = false }) => {
  const { pageId, slug } = useContext(PageContext)
  const activeItemRef = useRef<HTMLAnchorElement>(null)
  const asideClass = cn(styles.banner, {
    [styles.mobileEnabled]: mobileEnabled,
  })

  useEffect(() => {
    if (mobileEnabled && activeItemRef.current) {
      // Calculate scroll position after font load
      requestAnimationFrame(() =>
        activeItemRef.current?.scrollIntoView({
          block: 'nearest',
        })
      )
    }
  }, [mobileEnabled])

  return (
    <aside className={asideClass}>
      <nav className={styles.navigation}>
        {toolsSecondaryNavItems.map((item) => {
          const isActive = slug === item.slug
          return (
            <ExternalLink
              key={item.href}
              ref={mobileEnabled && isActive ? activeItemRef : null}
              className={cn(styles.navLink, { [styles.active]: isActive })}
              href={item.href}
              onClick={() => {
                void gnarSpec.linkClick(pageId, slug, item.href, item.label, 'Header', 'ToolsSecondaryNav')
              }}
            >
              <span className={styles.navLinkIcon}>
                <item.icon />
              </span>
              <span className={cn('font-family-inter', styles.navLinkText)}>{item.label}</span>
            </ExternalLink>
          )
        })}
      </nav>
    </aside>
  )
}
